import type {
	AbschnittCCandidateGender,
	AbschnittCCandidateIdMap,
	AbschnittCElectionCandidate,
	CandidateId
} from "@/lib/abschnittc/AbschnittCElection";
import type { EvaluatorContext } from "@/lib/abschnittc/impl/EvaluatorContext";
import { AbschnittCElectionEvaulationInterrupt } from "@/lib/abschnittc/impl/AbschnittCElectionEvaulationInterrupt";
import type { PreliminaryListSpot } from "@/lib/abschnittc/AbschnittCElectionResult";
import debug from "debug";

const logger = debug("abschnittc:AbschnittCElectionFinalListReader");

export interface AbschnittCElectionFinalListReaderOptions {
	candidates: AbschnittCElectionCandidate[];
	context: EvaluatorContext;
	acceptances: AbschnittCCandidateIdMap<boolean>;
	elected: CandidateId[];
	preliminaryResults: {
		male: PreliminaryListSpot[],
		female: PreliminaryListSpot[]
	};
}

export class AbschnittCElectionFinalListReader {

	constructor(private options: AbschnittCElectionFinalListReaderOptions) {
	}

	getCandidateOrThrow(id: CandidateId) {
		const candidate = this.options.candidates.find(v => v.id === id);

		if (!candidate)
			throw new Error(`Candidate with id ${id} not found`);

		return candidate;
	}

	text(message: string) {
		this.options.context.message("text", message);
	}

	candidateName(id: CandidateId) {
		return this.options.context.candidateName(id);
	}

	oppositeGender(gender: AbschnittCCandidateGender): AbschnittCCandidateGender {
		return gender === "male" ? "female" : "male";
	}

	candidatesForGender(gender: AbschnittCCandidateGender) {
		const out: CandidateId[] = [];

		for (const candidate of this.options.candidates) {
			if (candidate.list === gender)
				out.push(candidate.id);
		}

		return out;
	}

	forceAcceptanceOrDenial(id: CandidateId) {
		if (this.options.acceptances[id] === undefined)
			throw new AbschnittCElectionEvaulationInterrupt({ type: "acceptance-required", candidate: id });

		if (!this.options.acceptances[id])
			throw new Error("Candidate rejected");

		this.text(`Bewerber*in ${this.candidateName(id)} hat die Wahl angenommen.`);
	}

	nextCandidate() {

		const alreadyElected = [...this.options.elected];
		const nextSpot = alreadyElected.length + 1;
		const lastElected = alreadyElected[alreadyElected.length - 1];
		const nextGender = this.oppositeGender(this.getCandidateOrThrow(
			lastElected).list);

		this.text(`Listenplatz ${nextSpot} ist zu besetzen. Geschlecht: ${nextGender}`);

		const forGender = (gender: AbschnittCCandidateGender) => {
			return this.options.preliminaryResults[gender]
				.filter(value => !alreadyElected.includes(value.candidate));
		};

		const nextPossible = forGender(nextGender);

		if (nextPossible.length === 0) {
			nextPossible.push(...forGender(this.oppositeGender(nextGender)));
		}

		logger("nextPossible", nextPossible);

		const skipped: CandidateId[] = [];

		for (const nextResult of nextPossible) {
			const nextCandidateId = nextResult.candidate;
			const candidate = this.getCandidateOrThrow(nextCandidateId);
			const minSpot = candidate.minSpot;

			if (minSpot <= nextSpot) {
				// min spot of candidate is less or equal to the next spot
				// so this candidate could take the next spot
				this.forceAcceptanceOrDenial(nextCandidateId);

				this.options.context.message("minSpotMatch", nextSpot, skipped);
				return nextCandidateId;
			} else {
				skipped.push(nextCandidateId);
			}
		}
	}
}
